import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Input, 
  Table, 
  Row, 
  Col,
  Tag,
  Spin,
  Popover,
  Checkbox
} from 'antd';
import { 
  SearchOutlined,
  PlusOutlined,
  FilterOutlined,
  DownloadOutlined,
  UploadOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import AdminWordDetail from '@/pages/admin/word/detail';
import { adminWordApi } from '@/services/api';

const { Title } = Typography;
const { Search } = Input;
const DEFAULT_PAGE_SIZE = 10;

interface WordData {
  key: string;
  no: number;
  wordCode: string;
  word: string;
  ttsType: 'male' | 'female' | 'child';
  ttsGender: 'male' | 'female';
  ttsAge: 'child' | 'young' | 'adult' | 'elder';
  ttsSpeed: 'slow' | 'normal' | 'fast';
  ttsPitch: 'low' | 'medium' | 'high';
  registrant: string;
  registrationDate: string;
}

interface TtsFilterState {
  gender: string[];
  age: string[];
  speed: string[];
  pitch: string[];
}

const keywordMaps = {
  gender: {
    '남': 'male',
    '남성': 'male',
    '남자': 'male',
    '여': 'female',
    '여성': 'female',
    '여자': 'female',
  },
  age: {
    '아동': 'child',
    '청소년': 'young',
    '청소': 'young',
    '성인': 'adult',
    '어른': 'adult',
    '노년': 'elder',
    '노인': 'elder',
  },
  speed: {
    '느림': 'slow',
    '느린': 'slow',
    '보통': 'normal',
    '빠름': 'fast',
    '빠른': 'fast',
  },
  pitch: {
    '낮음': 'low',
    '낮은': 'low',
    '보통': 'medium',
    '중간': 'medium',
    '높음': 'high',
    '높은': 'high',
  },
} as const;

const AdminWordManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [loading, setLoading] = useState(false);
  const [filteredWords, setFilteredWords] = useState<WordData[]>([]);
  const [searchText, setSearchText] = useState(initialKeyword);
  const [ttsFilter, setTtsFilter] = useState<TtsFilterState>({
    gender: [],
    age: [],
    speed: [],
    pitch: []
  });
  const [ttsKeyword, setTtsKeyword] = useState('');
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [total, setTotal] = useState(0);
  const audioObjRef = useRef<HTMLAudioElement | null>(null);

  // 샘플 데이터
  const sampleWords: WordData[] = [
    {
      key: '1',
      no: 1,
      wordCode: 'a000001',
      word: 'apple',
      ttsType: 'female',
      ttsGender: 'female',
      ttsAge: 'adult',
      ttsSpeed: 'normal',
      ttsPitch: 'medium',
      registrant: '김관리자',
      registrationDate: '2024-01-15'
    },
    {
      key: '2',
      no: 2,
      wordCode: 'b000002',
      word: 'book',
      ttsType: 'male',
      ttsGender: 'male',
      ttsAge: 'adult',
      ttsSpeed: 'normal',
      ttsPitch: 'medium',
      registrant: '이관리자',
      registrationDate: '2024-01-16'
    },
    {
      key: '3',
      no: 3,
      wordCode: 'c000003',
      word: 'cat',
      ttsType: 'child',
      ttsGender: 'female',
      ttsAge: 'child',
      ttsSpeed: 'slow',
      ttsPitch: 'high',
      registrant: '박관리자',
      registrationDate: '2024-01-17'
    },
    {
      key: '4',
      no: 4,
      wordCode: 'd000004',
      word: 'dog',
      ttsType: 'female',
      ttsGender: 'female',
      ttsAge: 'young',
      ttsSpeed: 'fast',
      ttsPitch: 'medium',
      registrant: '최관리자',
      registrationDate: '2024-01-18'
    },
    {
      key: '5',
      no: 5,
      wordCode: 'e000005',
      word: 'elephant',
      ttsType: 'male',
      ttsGender: 'male',
      ttsAge: 'elder',
      ttsSpeed: 'slow',
      ttsPitch: 'low',
      registrant: '정관리자',
      registrationDate: '2024-01-19'
    },
    {
      key: '6',
      no: 6,
      wordCode: 'f000006',
      word: 'flower',
      ttsType: 'female',
      ttsGender: 'female',
      ttsAge: 'child',
      ttsSpeed: 'normal',
      ttsPitch: 'high',
      registrant: '김영어',
      registrationDate: '2024-01-20'
    },
    {
      key: '7',
      no: 7,
      wordCode: 'g000007',
      word: 'garden',
      ttsType: 'male',
      ttsGender: 'male',
      ttsAge: 'adult',
      ttsSpeed: 'normal',
      ttsPitch: 'medium',
      registrant: '이영어',
      registrationDate: '2024-01-21'
    },
    {
      key: '8',
      no: 8,
      wordCode: 'h000008',
      word: 'house',
      ttsType: 'female',
      ttsGender: 'female',
      ttsAge: 'young',
      ttsSpeed: 'fast',
      ttsPitch: 'medium',
      registrant: '박영어',
      registrationDate: '2024-01-22'
    },
    {
      key: '9',
      no: 9,
      wordCode: 'i000009',
      word: 'ice cream',
      ttsType: 'male',
      ttsGender: 'male',
      ttsAge: 'adult',
      ttsSpeed: 'slow',
      ttsPitch: 'low',
      registrant: '최영어',
      registrationDate: '2024-01-23'
    },
    {
      key: '10',
      no: 10,
      wordCode: 'j000010',
      word: 'jacket',
      ttsType: 'child',
      ttsGender: 'female',
      ttsAge: 'child',
      ttsSpeed: 'fast',
      ttsPitch: 'high',
      registrant: '정영어',
      registrationDate: '2024-01-24'
    }
  ];

  // 필터 옵션들 (더미 데이터 - 실제로는 API에서 가져와야 함)
  const genderOptions = [
    { value: 'male', label: '남성' },
    { value: 'female', label: '여성' }
  ];

  const ageOptions = [
    { value: 'child', label: '아동' },
    { value: 'young', label: '청소년' },
    { value: 'adult', label: '성인' },
    { value: 'elder', label: '노년' }
  ];

  const speedOptions = [
    { value: 'slow', label: '느림' },
    { value: 'normal', label: '보통' },
    { value: 'fast', label: '빠름' }
  ];

  const pitchOptions = [
    { value: 'low', label: '낮음' },
    { value: 'medium', label: '보통' },
    { value: 'high', label: '높음' }
  ];


  const parseTtsKeyword = (keyword: string) => {
    const tokens = keyword
      .split(/[\s,/]+/)
      .map((token) => token.trim())
      .filter(Boolean);
    const result: Partial<Record<'gender' | 'age' | 'speed' | 'pitch', string>> = {};
    tokens.forEach((token) => {
      if (keywordMaps.gender[token as keyof typeof keywordMaps.gender]) {
        result.gender = keywordMaps.gender[token as keyof typeof keywordMaps.gender];
      }
      if (keywordMaps.age[token as keyof typeof keywordMaps.age]) {
        result.age = keywordMaps.age[token as keyof typeof keywordMaps.age];
      }
      if (keywordMaps.speed[token as keyof typeof keywordMaps.speed]) {
        result.speed = keywordMaps.speed[token as keyof typeof keywordMaps.speed];
      }
      if (keywordMaps.pitch[token as keyof typeof keywordMaps.pitch]) {
        result.pitch = keywordMaps.pitch[token as keyof typeof keywordMaps.pitch];
      }
    });
    return result;
  };

  const resolveFilterValue = (arr: string[], derived?: string) => {
    if (derived) return derived;
    return arr.length === 1 ? arr[0] : '';
  };

  const syncSearchParams = useCallback((nextPage: number, nextSize: number, keyword: string) => {
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [setSearchParams]);

  const loadList = useCallback(async (pageIndex = 1, keyword = searchText, size = pageSize) => {
    setLoading(true);
    try {
      const params: any = {
        q: keyword,
        page: pageIndex - 1,
        size,
      };
      const derivedFilters = parseTtsKeyword(ttsKeyword);
      const genderValue = resolveFilterValue(ttsFilter.gender, derivedFilters.gender);
      const ageValue = resolveFilterValue(ttsFilter.age, derivedFilters.age);
      const speedValue = resolveFilterValue(ttsFilter.speed, derivedFilters.speed);
      const pitchValue = resolveFilterValue(ttsFilter.pitch, derivedFilters.pitch);
      if (genderValue) params.gender = genderValue;
      if (ageValue) params.age = ageValue;
      if (speedValue) params.speed = speedValue;
      if (pitchValue) params.pitch = pitchValue;

      const res: any = await adminWordApi.list(params);
      if (res?.success) {
        const { content = [], totalElements = 0 } = res.data || {};
        const mapped: WordData[] = content.map((it: any, idx: number) => ({
          key: String(it.id),
          no: totalElements - ((pageIndex - 1) * 10 + idx),
          wordCode: it.code || '',
          word: it.word,
          ttsType: it.gender,
          ttsGender: it.gender,
          ttsAge: it.age,
          ttsSpeed: it.speed,
          ttsPitch: it.pitch,
          registrant: it.registrant || '-',
          registrationDate: (it.createdAt || '').split('T')[0] || ''
        }));
        setFilteredWords(mapped);
        setTotal(totalElements);
        setPage(pageIndex);
        setPageSize(size);
      }
    } finally {
      setLoading(false);
    }
  }, [searchText, ttsKeyword, ttsFilter, pageSize]);

  // TTS 필터 적용 함수 (서버 검색 호출)

  const applyTtsFilter = () => {
    setPage(1);
    loadList(1, searchText, pageSize);
  };

  // TTS 필터 초기화
  const resetTtsFilter = () => {
    setTtsFilter({
      gender: [],
      age: [],
      speed: [],
      pitch: []
    });
    setTtsKeyword('');
  };

  // 초기화 함수
  const handleReset = () => {
    setSearchText('');
    resetTtsFilter();
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    loadList(1, '', DEFAULT_PAGE_SIZE);
  };

  // 선택된 필터 개수 계산
  const getSelectedFilterCount = () => {
    return ttsFilter.gender.length + ttsFilter.age.length + ttsFilter.speed.length + ttsFilter.pitch.length;
  };

  const handlePlay = async (record: WordData) => {
    try {
      const id = Number(record.key);
      if (!Number.isFinite(id)) return;
      const res: any = await adminWordApi.detail(id);
      const url = res?.data?.audioUrl || res?.audioUrl;
      if (!url) return;
      try { audioObjRef.current?.pause(); } catch {}
      const audio = new Audio();
      audio.src = url;
      audioObjRef.current = audio;
      await audio.play().catch(() => {});
    } catch {}
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<WordData> = [
    {
      title: '순번',
      dataIndex: 'no',
      key: 'no',
      width: 80,
      align: 'center'
    },
    {
      title: '단어코드',
      dataIndex: 'wordCode',
      key: 'wordCode',
      width: 120,
      align: 'center'
    },
    {
      title: '단어',
      dataIndex: 'word',
      key: 'word',
      width: 150,
      align: 'center'
    },
    {
      title: 'TTS 유형',
      key: 'ttsType',
      width: 200,
      align: 'center',
      render: (_, record) => {
        const genderText = record.ttsGender === 'male' ? '남성' : '여성';
        const ageText = {
          child: '아동',
          young: '청소년', 
          adult: '성인',
          elder: '노년'
        }[record.ttsAge];
        const speedText = {
          slow: '느림',
          normal: '보통',
          fast: '빠름'
        }[record.ttsSpeed];
        const pitchText = {
          low: '낮음',
          medium: '보통',
          high: '높음'
        }[record.ttsPitch];
        
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
            <div>{genderText} / {ageText}</div>
            <div>{speedText} / {pitchText}</div>
          </div>
        );
      }
    },
    {
      title: '등록자',
      dataIndex: 'registrant',
      key: 'registrant',
      width: 120,
      align: 'center'
    },
    {
      title: '등록일',
      dataIndex: 'registrationDate',
      key: 'registrationDate',
      width: 120,
      align: 'center'
    },
    {
      title: '미리듣기',
      key: 'preview',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button icon={<PlayCircleOutlined />} onClick={(e) => { e.stopPropagation(); handlePlay(record); }}>
          재생
        </Button>
      )
    }
  ];

  // 초기 데이터 로드
  useEffect(() => {
    syncSearchParams(page, pageSize, searchText);
    loadList(page, searchText, pageSize);
  }, [page, pageSize, searchText, loadList, syncSearchParams]);

  // TTS 필터 팝오버 컨텐츠
  const ttsFilterContent = (
    <div style={{ minWidth: '400px' }}>
      <Row gutter={16}>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>성별</h4>
          <Checkbox.Group
            options={genderOptions}
            value={ttsFilter.gender}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, gender: checkedValues as string[] }))}
          />
        </Col>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>연령대</h4>
          <Checkbox.Group
            options={ageOptions}
            value={ttsFilter.age}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, age: checkedValues as string[] }))}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>속도</h4>
          <Checkbox.Group
            options={speedOptions}
            value={ttsFilter.speed}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, speed: checkedValues as string[] }))}
          />
        </Col>
        <Col span={12}>
          <h4 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 'bold' }}>피치</h4>
          <Checkbox.Group
            options={pitchOptions}
            value={ttsFilter.pitch}
            onChange={(checkedValues) => setTtsFilter(prev => ({ ...prev, pitch: checkedValues as string[] }))}
          />
        </Col>
      </Row>
      <div style={{ marginTop: 16, textAlign: 'right', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        <Space>
          <Button size="small" onClick={resetTtsFilter}>
            초기화
          </Button>
          <Button size="small" type="primary" onClick={applyTtsFilter}>
            적용
          </Button>
        </Space>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
        {/* 헤더 */}
        <div style={{ marginBottom: 24 }}>
            <Title level={2} style={{ margin: 0 }}>단어 리스트</Title>
          {/* 검색 영역 */}
          <Row gutter={16} align="bottom" style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Search
                placeholder="단어 검색"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={applyTtsFilter}
                size="large"
                allowClear
              />
            </Col>
            <Col span={3}>
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={applyTtsFilter}
                size="large"
                style={{ width: '100%' }}
              >
                검색
              </Button>
            </Col>
            <Col span={3}>
              <Button 
                onClick={handleReset}
                size="large"
                style={{ width: '100%' }}
              >
                초기화
              </Button>
            </Col>
            <Col span={4}>
              <div>
                <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 'bold' }}>TTS 키워드 검색</div>
                <Input
                  placeholder="예) 남성 빠름 성인"
                  value={ttsKeyword}
                  onChange={(e) => setTtsKeyword(e.target.value)}
                  onPressEnter={applyTtsFilter}
                  allowClear
                  size="large"
                />
              </div>
            </Col>
            <Col span={4}>
              <div>
                <div style={{ marginBottom: 8, fontSize: '14px', fontWeight: 'bold' }}>
                  TTS 유형 필터
                </div>
                <Popover
                  content={ttsFilterContent}
                  title="TTS 유형 필터"
                  trigger="click"
                  placement="bottomLeft"
                  arrow={{ pointAtCenter: false }}
                >
                  <Button 
                    icon={<FilterOutlined />} 
                    size="large"
                    style={{ width: '100%' }}
                  >
                    필터 선택
                    {getSelectedFilterCount() > 0 && (
                      <Tag color="#1890ff" style={{ marginLeft: 8 }}>
                        {getSelectedFilterCount()}
                      </Tag>
                    )}
                  </Button>
                </Popover>
              </div>
            </Col>
          </Row>

          {/* 버튼 영역 */}
          <Row justify="end" align="bottom">
            <Space>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                onClick={() => navigate('/admin/word/create')}
              >
                새 단어 등록
              </Button>
            </Space>
          </Row>
        </div>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={filteredWords}
          loading={loading}
          pagination={{
            current: page,
            total: total,
            pageSize: pageSize,
            showTotal: (total) => {
              const start = Math.max(total - (page * pageSize) + 1, 1);
              const end = Math.max(total - ((page - 1) * pageSize), 1);
              return `${start}-${end} / 총 ${total}개 단어`;
            }
          }}
          scroll={{ x: 1000 }}
          size="middle"
          onRow={(record) => ({
            onClick: () => navigate(
              `/admin/word/detail/${record.key}`,
              { state: { from: `${location.pathname}${location.search}` } },
            ),
            style: { cursor: 'pointer' }
          })}
          onChange={(pagination) => {
            const nextPage = pagination.current || 1;
            const nextSize = pagination.pageSize || pageSize;
            setPage(nextPage);
            setPageSize(nextSize);
          }}
        />
      </div>
    </AdminLayout>
  );
};

export default AdminWordManagement; 